<template>
    <div class="page-container">
        <!-- Desktop View -->
        <div class="desktop-view" v-if="!isMobile">
            <o-bread-crumb :url="{ name: 'Member' }">
                <template v-slot:pageName>Member</template>
                <template v-slot:pageDetail>Transaction History</template>
            </o-bread-crumb>
            <div class="page-header">
                <div class="header-content">
                    <h1 class="page-title">Transactions History</h1>
                    <p class="page-subtitle">
                        History of {{ (page.clientDashboardData?.member_profile?.user?.first_name || page.clientDashboardData?.member_profile?.user?.last_name)
                                        ? page.clientDashboardData?.member_profile?.user?.title + ' ' + page.clientDashboardData?.member_profile?.user?.first_name + ' ' + page.clientDashboardData?.member_profile?.user?.last_name
                                        : 'your' }}'s transaction <span v-if="isPointSystemActive">& points</span>
                    </p>
                    <p v-if="filteredHistory.length === 0 && page.clientDashboardData?.member_profile?.user_profile?.created_at" class="text-muted mt-2" style="font-size: 0.98rem;">
                        {{ memberSinceText }}
                    </p>
                </div>
            </div>

            <div class="content-wrapper">
                <!-- Main Content -->
                <div class="main-content">
                    <!-- Skeleton Loading State (only on first load) -->
                    <SkeletonLoader v-if="page.isLoading && page.isFirstLoad" type="transaction" :count="5" />

                    <!-- Transaction List -->
                    <div v-if="page.output && !(page.isLoading && page.isFirstLoad)" class="transaction-list">
                        <my-transaction 
                            v-for="(item, index) in filteredHistory" 
                            :key="item.id || index"
                            :transaction="item"
                            :vouchers="vouchers"
                            @open-receipt="showModal(item)"
                        />
                        <no-items v-if="!filteredHistory.length" message="No transactions found" />
                    </div>

                    <!-- Pagination -->
                    <div class="container mt-5" v-if="!page.isLoading && filteredHistory.length">
                        <o-pagination 
                            :page="meta.page" 
                            :total="meta.total" 
                            @callback="navPage"
                        />
                    </div>
                </div>

                <!-- Sidebar Filter & Sort -->
                <div class="sidebar-filter">
                    <!-- Filter Section -->
                    <div class="filter-card card shadow-none">
                        <h3 class="card-title">Filter</h3>
                        
                        <div class="filter-group">
                            <label class="filter-label">Unit / Branch</label>
                            <div class="custom-select">
                                <select v-model="selectedBranch" @change="updateMerchants">
                                    <option value="">All Branch</option>
                                    <option v-for="branch in branches" :key="branch.id" :value="branch.name">
                                        {{ branch.name }}
                                    </option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Merchant / Outlet</label>
                            <div class="custom-select">
                                <select v-model="selectedMerchant">
                                    <option value="">All Merchant</option>
                                    <option v-for="merchant in filteredMerchants" :key="merchant.id" :value="merchant.name">
                                        {{ merchant.name }}
                                    </option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Date Range</label>
                            <div class="date-range-inputs">
                                <DatePicker
                                    v-model="startDate"
                                    :max="endDate"
                                    placeholder="Start Date"
                                    class="date-picker"
                                />
                                <span class="date-separator">to</span>
                                <DatePicker
                                    v-model="endDate"
                                    :min="startDate"
                                    placeholder="End Date"
                                    class="date-picker"
                                />
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Type</label>
                            <div class="custom-select">
                                <select v-model="selectedType">
                                    <option value="">All Type</option>
                                    <option value="normal">Normal</option>
                                    <option value="gift" v-if="isVoucherGiftActive">Gift</option>
                                    <option value="pending">Pending</option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="toggle-group" v-if="selectedType !== 'pending' && selectedType !== 'normal'">
                            <label class="toggle-container">
                                <input type="checkbox" v-model="hidePending" class="toggle-input">
                                <span class="toggle-slider"></span>
                            </label>
                            <span class="toggle-label">Hide Pending Transaction</span>
                        </div>

                        <div class="toggle-group" v-if="selectedType !== 'gift' && selectedType !== 'normal' && isVoucherGiftActive">
                            <label class="toggle-container">
                                <input type="checkbox" v-model="hideGift" class="toggle-input">
                                <span class="toggle-slider"></span>
                            </label>
                            <span class="toggle-label">Hide Gift Transaction</span>
                        </div>

                        <div class="button-group">
                            <button @click="applyFilter" class="btn btn-primary btn-apply">Apply Filter</button>
                            <button @click="clearFilter" class="btn btn-outline-secondary btn-clear">Clear Filter</button>
                        </div>
                    </div>

                    <!-- Sort Section -->
                    <div v-if="isPointSystemActive" class="filter-card card shadow-none mt-3">
                        <h3 class="card-title">Sort</h3>
                        
                        <div class="filter-group">
                            <label class="filter-label">Sort By</label>
                            <div class="custom-select">
                                <select v-model="sortBy">
                                    <option value="" disabled>Sort By</option>
                                    <option value="pointRequirement">Point</option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Sort Direction</label>
                            <div class="custom-select">
                                <select v-model="sortDirection">
                                    <option value="" disabled>Direction</option>
                                    <option value="asc">Lowest to Highest</option>
                                    <option value="desc">Highest to Lowest</option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="button-group">
                            <button @click="applySort" class="btn btn-primary btn-apply">Apply Sort</button>
                            <button @click="clearSort" class="btn btn-outline-secondary btn-clear">Clear Sort</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Mobile View -->
        <div class="mobile-view" v-else>
            <div class="mobile-header">
                <h2 class="mobile-title">All Transactions</h2>
                <div class="mobile-actions">
                    <button @click="openModal('filter')" class="filter-button btn btn-primary">
                        <img src="/img/member-icon/filter-icon.svg" alt="filter-icon" width="14" height="14">
                    </button>
                    <button v-if="isPointSystemActive" @click="openModal('sort')" class="filter-button btn btn-primary">
                        <img src="/img/member-icon/sort-icon.svg" alt="sort-icon" width="14" height="14">
                    </button>
                </div>
            </div>

            <!-- Skeleton Loading State (Mobile - only on first load) -->
            <SkeletonLoader v-if="page.isLoading && page.isFirstLoad" type="transaction" :count="4" />

            <!-- Transaction List -->
            <div v-if="page.output && !(page.isLoading && page.isFirstLoad)" class="mobile-transaction-list">
                <my-transaction 
                    v-for="(item, index) in filteredHistory" 
                    :key="item.id || index"
                    :transaction="item"
                    :vouchers="vouchers"
                    @open-receipt="showModal(item)"
                />
                <no-items v-if="!filteredHistory.length" message="No transactions found" />
            </div>

            <!-- Pagination -->
            <div class="container mt-5" v-if="!page.isLoading && filteredHistory.length">
                <o-pagination 
                    :page="meta.page" 
                    :total="meta.total" 
                    @callback="navPage"
                />
            </div>

            <!-- Filter Modal -->
            <div v-if="isFilterModalOpen" class="modal-overlay" @click="closeModal">
                <div class="modal-content" @click.stop>
                    <div class="modal-header">
                        <h3>Filter</h3>
                        <button @click="closeModal" class="close-button">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                        </button>
                    </div>

                    <div class="modal-body">
                        <div class="filter-group">
                            <label class="filter-label">Unit / Branch</label>
                            <div class="custom-select">
                                <select v-model="selectedBranch" @change="updateMerchants">
                                    <option value="">All Branch</option>
                                    <option v-for="branch in branches" :key="branch.id" :value="branch.name">
                                        {{ branch.name }}
                                    </option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Merchant / Outlet</label>
                            <div class="custom-select">
                                <select v-model="selectedMerchant">
                                    <option value="">All Merchant</option>
                                    <option v-for="merchant in filteredMerchants" :key="merchant.id" :value="merchant.name">
                                        {{ merchant.name }}
                                    </option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Date Range</label>
                            <div class="date-range-inputs mobile">
                                <DatePicker
                                    v-model="startDate"
                                    :max="endDate"
                                    placeholder="Start Date"
                                    class="date-picker"
                                />
                                <span class="date-separator">to</span>
                                <DatePicker
                                    v-model="endDate"
                                    :min="startDate"
                                    placeholder="End Date"
                                    class="date-picker"
                                />
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Type</label>
                            <div class="custom-select">
                                <select v-model="selectedType">
                                    <option value="">All Type</option>
                                    <option value="normal">Normal</option>
                                    <option value="gift">Gift</option>
                                    <option value="pending">Pending</option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="toggle-group" v-if="selectedType !== 'pending' && selectedType !== 'normal'">
                            <label class="toggle-container">
                                <input type="checkbox" v-model="hidePending" class="toggle-input">
                                <span class="toggle-slider"></span>
                            </label>
                            <span class="toggle-label">Hide Pending Transaction</span>
                        </div>

                        <div class="toggle-group" v-if="selectedType !== 'gift' && selectedType !== 'normal'">
                            <label class="toggle-container">
                                <input type="checkbox" v-model="hideGift" class="toggle-input">
                                <span class="toggle-slider"></span>
                            </label>
                            <span class="toggle-label">Hide Gift Transaction</span>
                        </div>

                        <div class="button-group">
                            <button @click="applyFilter" class="btn btn-primary btn-apply">Apply Filter</button>
                            <button @click="clearFilter" class="btn btn-outline-secondary btn-clear">Clear Filter</button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Sort Modal -->
            <div v-if="isSortModalOpen && isPointSystemActive" class="modal-overlay" @click="closeModal">
                <div class="modal-content" @click.stop>
                    <div class="modal-header">
                        <h3>Sort</h3>
                        <button @click="closeModal" class="close-button">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                        </button>
                    </div>

                    <div class="modal-body">
                        <div class="filter-group">
                            <label class="filter-label">Sort By</label>
                            <div class="custom-select">
                                <select v-model="sortBy">
                                    <option value="" disabled>Sort By</option>
                                    <option value="pointRequirement">Point</option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Sort Direction</label>
                            <div class="custom-select">
                                <select v-model="sortDirection">
                                    <option value="" disabled>Direction</option>
                                    <option value="asc">Lowest to Highest</option>
                                    <option value="desc">Highest to Lowest</option>
                                </select>
                                <svg class="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        <div class="button-group">
                            <button @click="applySort" class="btn btn-primary btn-apply">Apply Sort</button>
                            <button @click="clearSort" class="btn btn-outline-secondary btn-clear">Clear Sort</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Transaction Receipt Modal -->
        <transaction-client
            v-if="isModalVisible"
            @parentCloseModal="closeModal"
            :editedItem="editedItem">
        </transaction-client>
    </div>
</template>

<script>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { usePointSystem } from '@/composables/pointsystem'
import {OBreadCrumb, OPagination} from '@/components/OmnihControl'
import TransactionClient from './member/MemberTransaction.vue'
import MyTransaction from '@/components/member/MyTransaction.vue'
import DatePicker from '@/components/DatePicker.vue'
import SkeletonLoader from '@/components/member/SkeletonLoader.vue'
import NoItems from '@/components/member/NoItems.vue'

export default {
    components: {
        TransactionClient,
        MyTransaction,
        DatePicker,
        SkeletonLoader,
        NoItems,
        OPagination,
        OBreadCrumb
    },
    setup() {
        const store = useStore()
        const route = useRoute()
        const { isPointSystemActive } = usePointSystem()
        const isModalVisible = ref(false)
        const editedItem = ref({
            id: '',
            name: '',
            transaction_value: '',
            title: ''
        })

        const page = reactive({
            isLoading: true,
            output: false,
            clientDashboardData: null,
            isFirstLoad: true
        })

        const meta = ref({
            page: 1,
            total: 0,
            limit: 10
        });

        const historyData = ref([])
        const allData = ref([])
        const branches = ref([])
        const filteredMerchants = ref([])
        const selectedBranch = ref('')
        const selectedMerchant = ref('')
        const startDate = ref(null)
        const endDate = ref(null)
        const selectedType = ref('')
        const hidePending = ref(false)
        const hideGift = ref(false)
        const sortBy = ref('')
        const sortDirection = ref('')
        const filteredData = ref([])
        const vouchers = ref([])

        const filteredHistory = computed(() => filteredData.value)
        const isFilterModalOpen = ref(false)
        const isSortModalOpen = ref(false)
        const isMobile = ref(window.innerWidth <= 900)
        const isVoucherGiftActive = computed(() => store.getters['FeatureFlag/getVoucherGiftFlag'] !== 'NONACTIVE')

        window.addEventListener('resize', () => {
            isMobile.value = window.innerWidth <= 900
        })

        const openModal = (type) => {
            if (type === 'filter') {
                isFilterModalOpen.value = true
                isSortModalOpen.value = false
            } else if (type === 'sort') {
                isSortModalOpen.value = true
                isFilterModalOpen.value = false
            }
        }

        const clearFilter = () => {
            selectedBranch.value = ''
            selectedMerchant.value = ''
            selectedType.value = ''
            startDate.value = null
            endDate.value = null
            hidePending.value = false
            hideGift.value = false
            loadState()
            closeModal()
        }

        const applySort = () => {
            applyFilter()
            closeModal()
        }

        const clearSort = () => {
            sortBy.value = ''
            sortDirection.value = ''
            applySort()
            closeModal()
        }

        const applyFilter = () => {
            const _meta = { ...meta.value, page: 1 }
            store.commit('Member/switch_page', { meta: _meta })
            loadState()
        }

        onMounted(() => {
            store.commit('Member/reset_meta');
            loadState()
        })

        function showModal(data) {
            function getBusinessLogo() {
                try {
                    if (!page.clientDashboardData) {
                        return null;
                    }

                    if (!page.clientDashboardData.branch_images || 
                        !Array.isArray(page.clientDashboardData.branch_images) || 
                        page.clientDashboardData.branch_images.length === 0) {
                        return null;
                    }

                    const firstBranch = page.clientDashboardData.branch_images[0];
                    if (!firstBranch || !firstBranch.business_profile || !firstBranch.business_profile.logo) {
                        return null;
                    }

                    return firstBranch.business_profile.logo.image_url || null;
                } catch (error) {
                    return null;
                }
            }

            editedItem.value = {
                id: data.id,
                member_name: data.member_name,
                member_email: page.clientDashboardData.member_profile.user.email,
                phone: page.clientDashboardData.member_profile.user_profile.phone,
                state: page.clientDashboardData.member_profile.user_profile.state,
                address: page.clientDashboardData.member_profile.user_profile.address,
                country: page.clientDashboardData.member_profile.user_profile.country,
                branch_name: data.branch_name,
                merchant_name: data.merchant_name,
                type: data.type,
                created_at: data.created_at,
                qty: data.qty,
                detail_name: data.detail_name || 'Deduction Point',
                detail_code: data.detail_code,
                transaction_code: data.transaction_code,
                transaction_value: data.subtotal,
                voucher_discount: data.voucher_discount,
                amount_after_voucher: data.amount_after_voucher,
                voucher_detail: data.voucher_detail,
                point: (data.point !== undefined && data.point !== null)
                    ? data.point
                    : (data.point_deducted !== undefined && data.point_deducted !== null)
                        ? `-${Math.abs(data.point_deducted)}`
                        : null,
                business_logo: getBusinessLogo(),
                reference_code: data.refference_code,
                tax_charge: data.tax_charge,
                service_charge: data.service_charge,
                tax_value: data.tax_value,
                service_value: data.service_value,
                before_tax_service: data.before_tax_service,
                after_tax_service: data.after_tax_service,
                is_nett_price: data.is_nett_price,
                title: 'Transaction Recepient'
            }
            isModalVisible.value = true
        }

        function closeModal() {
            isModalVisible.value = false
            isFilterModalOpen.value = false
            isSortModalOpen.value = false
        }

        const loadState = () => {
            page.isLoading = true
            const filter = {}

            if (selectedBranch.value) {
                filter.branch_id = selectedBranch.value
            }

            if (selectedMerchant.value) {
                filter.merchant_id = selectedMerchant.value
            }

            if (startDate.value && endDate.value) {
                const formattedStart = startDate.value
                const formattedEnd = endDate.value
                filter.dateRange = `${formattedStart}, ${formattedEnd}`
            }

            if (selectedType.value) {
                filter.type = selectedType.value
            }

            if (hidePending.value) {
                filter.hide_pending = true
            }

            if (hideGift.value) {
                filter.hide_gift = true
            }

            if (sortBy.value && sortDirection.value) {
                filter.sortBy = sortBy.value
                filter.sortDirection = sortDirection.value
            }

            store.commit('Member/add_filter', { filter })

            // Fetch deals/vouchers dictionary for transaction icons (Name resolution)
            store.dispatch('ClientDeals/read')
                .then(response => {
                    if (response.data && response.data.data && response.data.data.voucher) {
                        vouchers.value = response.data.data.voucher
                    }
                })
                .catch(err => console.log('Failed to load deals dictionary:', err))

            store.dispatch('Member/read_transaction_history', route.params.id)
                .then((res) => {
                    historyData.value = res.all_data;
                    allData.value = res.all_data;
                    filteredData.value = res.all_data;
                    
                    page.clientDashboardData = {
                        member_profile: res.member_profile,
                        branch_images: res.branch_images 
                    };

                    meta.value = {
                        page: res.meta?.page || 1,
                        total: res.meta?.total || 0,
                        limit: res.meta?.limit || 10
                    };

                    branches.value = res.filter.filter.map(branchData => ({
                        id: branchData.id,
                        name: branchData.name,
                        merchants: branchData.merchants
                    }));

                    if (branches.value.length > 0 && !selectedBranch.value) {
                        selectedBranch.value = ''
                    }

                    updateMerchants()
                    if (isMobile.value) {
                        closeModal()
                    }
                })
                .catch((err) => {
                    console.log(err)
                })
                .finally(() => {
                    page.isLoading = false
                    page.output = true
                    page.isFirstLoad = false
                })
        }

        const updateMerchants = () => {
            if (selectedBranch.value === '') {
                let allMerchants = []
                branches.value.forEach(branch => {
                    allMerchants = allMerchants.concat(branch.merchants)
                })
                filteredMerchants.value = allMerchants
            } else {
                const selected = branches.value.find(branch => branch.name === selectedBranch.value)
                filteredMerchants.value = selected ? selected.merchants : []
            }

            if (!selectedMerchant.value) {
                selectedMerchant.value = ''
            }
        }

        const navPage = (newPage) => {
            const _meta = { ...meta.value, page: newPage }
            store.commit('Member/switch_page', { meta: _meta })
            loadState()
        }

        const memberSinceText = computed(() => {
            const createdAt = page.clientDashboardData?.member_profile?.user_profile?.created_at;
            if (!createdAt) return '';
            const dateObj = new Date(createdAt);
            const options = { day: '2-digit', month: 'long', year: 'numeric' };
            const formatted = dateObj.toLocaleDateString('id-ID', options);
            return `This member has not made any transactions since registering. Member since: ${formatted}`;
        });

        watch(
            () => selectedBranch.value,
            (newVal) => {
                if (newVal === '') {
                    selectedMerchant.value = ''
                }
            }
        )

        return {
            page,
            meta,
            loadState,
            navPage,
            isModalVisible,
            editedItem,
            showModal,
            closeModal,
            filteredHistory,
            branches,
            filteredMerchants,
            selectedBranch,
            selectedMerchant,
            updateMerchants,
            startDate,
            endDate,
            selectedType,
            hidePending,
            hideGift,
            applyFilter,
            clearFilter,
            applySort,
            clearSort,
            sortBy,
            sortDirection,
            isMobile,
            isFilterModalOpen,
            isSortModalOpen,
            openModal,
            vouchers,
            isPointSystemActive,
            memberSinceText,
            isVoucherGiftActive
        }
    }
}
</script>

<style scoped>
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}


.filter-button {
    border: none;
    padding: 6px 10px;
    border-radius: 8px;
    transition: background-color 0.3s;
}

.filter-button img {
    width: 14px;
    height: 14px;
}


.top-background {
    position: absolute;
    top: -8px;
    left: -32px;
}

.header-text-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    color: white;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
}

.welcome-text {
    font-size: 10px;
    font-weight: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
}

.member-name-title {
    font-size: 1.2rem;
    font-weight: 520;
    letter-spacing: -2%;
    word-break: break-word;
    overflow-wrap: anywhere;
    line-height: 1.3;
}

.page-container {
    width: 100%;
    min-height: 100vh;
}

/* Desktop View */
.desktop-view {
    margin: 0 auto;
    padding: 24px;
}

.page-header {
    margin-bottom: 32px;
}

.page-title {
    font-size: 28px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 8px;
}

.page-subtitle {
    font-size: 14px;
    color: #6B7280;
}

.content-wrapper {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 24px;
}

.main-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.transaction-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.sidebar-filter {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.filter-card {
    background: white;
    border-radius: 8px;
    padding: 20px;
    /* Box shadow usually supplied by bootstrap component or Admin styling */
}

.card-title {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 20px;
}

.filter-group {
    margin-bottom: 20px;
}

.filter-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 8px;
}

.custom-select {
    position: relative;
}

.custom-select select {
    width: 100%;
    padding: 12px 40px 12px 16px;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    font-size: 14px;
    color: #111827;
    background: white;
    cursor: pointer;
    appearance: none;
    outline: none;
    transition: all 0.2s;
}

.custom-select select:hover {
    border-color: #D1D5DB;
}

.custom-select select:focus {
    border-color: var(--primary-color, #F93E3E);
    box-shadow: 0 0 0 3px var(--secondary-color, rgba(249, 62, 62, 0.15));
}

.select-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #6B7280;
}

.date-range-inputs {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 8px;
}

.date-input {
    min-width: 0; /* Prevent overflow */
    padding: 12px 16px;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    font-size: 14px;
    color: #111827;
    outline: none;
    transition: all 0.2s;
    width: 100%;
    box-sizing: border-box;
}

.date-input:hover {
    border-color: #D1D5DB;
}

.date-input:focus {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light, rgba(249, 62, 62, 0.15));
}

.date-separator {
    color: #6B7280;
    font-size: 14px;
}

.toggle-group {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
}

.toggle-container {
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;
}

.toggle-input {
    opacity: 0;
    width: 0;
    height: 0;
}

.toggle-slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #E5E7EB;
    transition: .4s;
    border-radius: 24px;
}

.toggle-slider:before {
    position: absolute;
    content: "";
    height: 20px;
    width: 20px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.toggle-input:checked + .toggle-slider {
    background-color: var(--primary-color);
}

.toggle-input:checked + .toggle-slider:before {
    transform: translateX(20px);
}

.toggle-label {
    font-size: 14px;
    color: #374151;
}

.button-group {
    display: flex;
    gap: 12px;
}

.btn-apply, .btn-clear {
    flex: 1;
    padding: 12px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-apply {
    color: white;
    border: none;
}

.btn-clear {
    background: white;
    color: #6B7280;
    border: 1px solid #E5E7EB;
}

.btn-clear:hover {
    background: #F9FAFB;
    color: #374151;
}

.btn-clear:active,
.btn-clear:focus,
.btn-clear:active:focus {
    background: #F3F4F6 !important;
    color: #374151 !important;
    border-color: #D1D5DB !important;
    box-shadow: none !important;
}

/* Mobile View Styles */
.mobile-view {
    padding: 16px;
    position: relative;
    overflow-x: hidden;
}

.mobile-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    position: relative;
    z-index: 10;
}

.mobile-title {
    font-size: 20px;
    font-weight: 600;
    margin: 0;
}

.mobile-actions {
    display: flex;
    gap: 8px;
    margin-right: 52px; /* Avoid overlapping the global avatar icon */
}

.mobile-transaction-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
    z-index: 10;
    padding-bottom: 24px;
}

.transaction-list :deep(.transaction-container),
.mobile-transaction-list :deep(.transaction-container) {
    border: 1px solid #E5E7EB;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: flex-end;
    z-index: 1000;
    animation: fadeIn 0.2s ease-out;
}

.modal-content {
    background: white;
    width: 100%;
    border-radius: 24px 24px 0 0;
    padding: 24px;
    animation: slideUp 0.3s ease-out;
    max-height: 90vh;
    overflow-y: auto;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
}

.modal-header h3 {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
    margin: 0;
}

.close-button {
    background: none;
    border: none;
    color: #6B7280;
    cursor: pointer;
    padding: 4px;
}

.close-button:hover {
    color: #111827;
}

/* Date Picker Mobile Adjustments */
.date-range-inputs.mobile {
    grid-template-columns: 1fr;
    gap: 12px;
}

.date-range-inputs.mobile .date-separator {
    display: none;
}

@keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@media (max-width: 900px) {
    .page-container {
        background-color: #F9FAFB;
    }
}
</style>